import { FC, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Autocomplete
} from "@mui/material";
import { Groups } from "@mui/icons-material";
import { mftCompanies } from "../../../../../../utils/consts";
import {
  CommentDto,
  TradingCounterParty,
  VpDto
} from "../../../../../../api/Api";
import { VpPicker } from "../../../../../../components/vpPicker";
import {
  useAssignees,
  useCounterpartyProgressOverride,
  useUpdateCounterparty
} from "../../../../dataHooks/counterpartyQueries";
import { Progress } from "../../../../../../components/lifecycle/departmentProgress/utils";
import {
  createLifecycleActions,
  LifecycleAction
} from "../../../../../../components/lifecycle/createLifecycleActions";
import { CommentsSection } from "../../sharedComponents/commentsSection";
import { LifecycleDialog } from "../../sharedComponents/lifecycleDialog";
import { SignOffNoteField } from "../../../../../../components/lifecycle/signOffNoteField";
import { useLifecycleComments } from "../../../../../../components/lifecycle/useLifecycleComments";
import { useRoles } from "../../../../../../auth/useRoles";
import { ownsLifecycle } from "../../../../../../auth/permissions";

type Props = {
  specificCounterparty: TradingCounterParty;
};

type FormData = {
  marketAccessAssignee: string;
  counterpartyName: string;
  counterpartyType: string;
  contractualMFTTeam: string;
  kycCheckOnMft: string;
  kycCheckOnCounterparty: string;
  agreementType: string;
  agreementReview: string;
  ndaReview: string;
  mandate: string;
  collateral: string;
  tradeCapture: string;
  tradeConfirmation: string;
  comments: CommentDto[];
  signOffNote: string;
};

export const MarketAccess: FC<Props> = ({ specificCounterparty }) => {
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const roles = useRoles();
  const progressOverride = useCounterpartyProgressOverride(
    specificCounterparty,
    "marketAccess"
  );

  const marketAccessAssignees = useAssignees("marketAccess");

  // Kept out of FormData on purpose: FormData is spread wholesale into
  // marketAccessLifecycle, and the VP is a root level field.
  const [vp, setVp] = useState<VpDto | null>(specificCounterparty?.vp ?? null);

  const [formData, setFormData] = useState<FormData>({
    counterpartyName: specificCounterparty?.counterpartyName || "",
    counterpartyType: specificCounterparty?.counterpartyType || "",
    contractualMFTTeam: specificCounterparty?.tradingTeam || "",
    marketAccessAssignee:
      specificCounterparty?.marketAccessLifecycle?.marketAccessAssignee || "",
    kycCheckOnMft:
      specificCounterparty?.marketAccessLifecycle?.kycCheckOnMft || "",
    kycCheckOnCounterparty:
      specificCounterparty?.marketAccessLifecycle?.kycCheckOnCounterparty || "",
    agreementType:
      specificCounterparty?.marketAccessLifecycle?.agreementType || "",
    agreementReview:
      specificCounterparty?.marketAccessLifecycle?.agreementReview || "",
    ndaReview: specificCounterparty?.marketAccessLifecycle?.ndaReview || "",
    mandate: specificCounterparty?.marketAccessLifecycle?.mandate || "",
    collateral: specificCounterparty?.marketAccessLifecycle?.collateral || "",
    tradeCapture:
      specificCounterparty?.marketAccessLifecycle?.tradeCapture || "",
    tradeConfirmation:
      specificCounterparty?.marketAccessLifecycle?.tradeConfirmation || "",
    comments: specificCounterparty?.marketAccessLifecycle?.comments || [],
    signOffNote: specificCounterparty?.marketAccessLifecycle?.signOffNote || ""
  });

  const handleClose = () => {
    navigate(`/tradingcounterparties/counterparty/${params.id}`);
  };
  const updateCounterparty = useUpdateCounterparty({
    onSuccess: handleClose
  });
  const commentUpdate = useUpdateCounterparty();

  const buildCounterparty = (
    values: FormData & { complete?: boolean },
    action: LifecycleAction = "save"
  ): TradingCounterParty => {
    const techAutoComplete =
      action === "complete" && values.tradeCapture === "not_needed";
    return {
      ...specificCounterparty,
      counterpartyName:
        values.counterpartyName ?? specificCounterparty.counterpartyName,
      counterpartyType:
        values.counterpartyType ?? specificCounterparty.counterpartyType,
      tradingTeam:
        values.contractualMFTTeam ?? specificCounterparty.tradingTeam,
      vp,
      marketAccessLifecycle: {
        ...specificCounterparty.marketAccessLifecycle,
        ...values
      },
      ...(techAutoComplete
        ? {
            progress: {
              ...specificCounterparty.progress,
              tech: Progress.Completed
            },
            techLifecycle: {
              ...specificCounterparty.techLifecycle,
              complete: true
            }
          }
        : {})
    };
  };

  const {
    currentComment,
    setCurrentComment,
    addComment,
    deleteComment,
    isSaving
  } = useLifecycleComments({
    formData,
    setFormData,
    update: commentUpdate,
    buildEntityWithComments: (comments) =>
      buildCounterparty({ ...formData, comments })
  });

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const { handleSave, handleComplete } = createLifecycleActions({
    formData,
    currentComment,
    currentProgress: specificCounterparty.progress?.marketAccess,
    canSetInProgress: ownsLifecycle(roles, "marketaccess"),
    buildEntity: (values, progress, action) => {
      const saved = buildCounterparty(values, action);
      // Keep any cross-lifecycle progress changes made by the payload builder.
      return {
        ...saved,
        progress: { ...saved.progress, marketAccess: progress }
      };
    },
    mutate: updateCounterparty.mutate
  });

  const open = location.pathname.includes("marketaccess");

  const canComplete =
    formData.counterpartyName &&
    formData.counterpartyType &&
    formData.contractualMFTTeam &&
    formData.kycCheckOnMft &&
    formData.kycCheckOnCounterparty &&
    formData.agreementType &&
    formData.agreementReview &&
    formData.ndaReview &&
    formData.mandate &&
    formData.collateral &&
    formData.tradeCapture;

  return (
    <LifecycleDialog
      open={open}
      onClose={handleClose}
      title="Market Access"
      progressOverride={progressOverride}
      maxWidth="md"
      headerContent={
        <>
          <Box display="flex" gap={2}>
            <Typography variant="subtitle1" component="span">
              Requested by:
            </Typography>
            <Typography variant="subtitle1" component="span" fontWeight="bold">
              {specificCounterparty?.contactPersonTradingTeam}
            </Typography>
            <Typography variant="subtitle1" component="span" fontWeight="bold">
              {specificCounterparty?.tradingTeam}
            </Typography>
          </Box>
          <Box sx={{ paddingY: 2 }}>
            <Autocomplete
              sx={{ flex: 1, alignSelf: "center" }}
              options={marketAccessAssignees.data || []}
              getOptionLabel={(option) => `${option.name} (${option.email})`}
              value={
                marketAccessAssignees.data?.find(
                  (a) => a.email === formData.marketAccessAssignee
                ) || null
              }
              onChange={(_, newValue) =>
                handleChange("marketAccessAssignee", newValue?.email || "")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Market Access assignee"
                  size="medium"
                />
              )}
            />
          </Box>
          <Box sx={{ paddingBottom: 2 }}>
            <VpPicker value={vp} onChange={setVp} />
          </Box>
          <Divider flexItem />
        </>
      }
      actions={{
        onClose: handleClose,
        onSave: handleSave,
        onComplete: handleComplete,
        canComplete: !!canComplete,
        isSaving: updateCounterparty.isPending
      }}
    >
      <Box sx={{ paddingTop: 1 }} gap={2} display="flex" flexDirection="column">
        <TextField
          value={formData.counterpartyName}
          onChange={(e) => handleChange("counterpartyName", e.target.value)}
          label="Counterparty name"
          size="medium"
          required
        />

        <TextField
          value={formData.counterpartyType}
          onChange={(e) => handleChange("counterpartyType", e.target.value)}
          label="Counterparty type"
          size="medium"
          required
        />

        <FormControl fullWidth required>
          <InputLabel>Contractual MFT Team</InputLabel>
          <Select
            value={formData.contractualMFTTeam}
            onChange={(e) => handleChange("contractualMFTTeam", e.target.value)}
            label="Contractual MFT Team"
            startAdornment={
              <InputAdornment position="start">
                <Groups fontSize="small" />
              </InputAdornment>
            }
          >
            {mftCompanies.map((company) => {
              const split = company.split("MFT");
              return (
                <MenuItem key={company} value={company}>
                  <Typography variant="body1" component="span">
                    {"MFT" + split[1]}
                  </Typography>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <Divider />

        <FormControl size="medium" required>
          <InputLabel>KYC Check (on MFT)</InputLabel>
          <Select
            label="KYC Check (on MFT)"
            value={formData.kycCheckOnMft}
            onChange={(e) => handleChange("kycCheckOnMft", e.target.value)}
          >
            <MenuItem value="needed">
              <Typography variant="body1" component="span">
                Needed
              </Typography>
            </MenuItem>
            <MenuItem value="not_needed">
              <Typography variant="body1" component="span">
                Not needed
              </Typography>
            </MenuItem>
          </Select>
        </FormControl>

        <FormControl size="medium" required>
          <InputLabel>
            KYC Check/sanction screening (on Counterparty)
          </InputLabel>
          <Select
            label="KYC Check/sanction screening (on Counterparty)"
            value={formData.kycCheckOnCounterparty}
            onChange={(e) =>
              handleChange("kycCheckOnCounterparty", e.target.value)
            }
          >
            <MenuItem value="needed">
              <Typography variant="body1" component="span">
                Needed
              </Typography>
            </MenuItem>
            <MenuItem value="not_needed">
              <Typography variant="body1" component="span">
                Not needed
              </Typography>
            </MenuItem>
          </Select>
        </FormControl>

        <TextField
          value={formData.agreementType}
          onChange={(e) => handleChange("agreementType", e.target.value)}
          label="Agreement Type"
          size="medium"
          required
        />

        <FormControl size="medium" required>
          <InputLabel>Agreement Review</InputLabel>
          <Select
            label="Agreement Review"
            value={formData.agreementReview}
            onChange={(e) => handleChange("agreementReview", e.target.value)}
          >
            <MenuItem value="needed">
              <Typography variant="body1" component="span">
                Needed
              </Typography>
            </MenuItem>
            <MenuItem value="not_needed">
              <Typography variant="body1" component="span">
                Not needed
              </Typography>
            </MenuItem>
          </Select>
        </FormControl>

        <FormControl size="medium" required>
          <InputLabel>NDA Review</InputLabel>
          <Select
            label="NDA Review"
            value={formData.ndaReview}
            onChange={(e) => handleChange("ndaReview", e.target.value)}
          >
            <MenuItem value="needed">
              <Typography variant="body1" component="span">
                Needed
              </Typography>
            </MenuItem>
            <MenuItem value="not_needed">
              <Typography variant="body1" component="span">
                Not needed
              </Typography>
            </MenuItem>
          </Select>
        </FormControl>

        <FormControl size="medium" required>
          <InputLabel>Mandate</InputLabel>
          <Select
            label="Mandate"
            value={formData.mandate}
            onChange={(e) => handleChange("mandate", e.target.value)}
          >
            <MenuItem value="needed">
              <Typography variant="body1" component="span">
                Needed
              </Typography>
            </MenuItem>
            <MenuItem value="not_needed">
              <Typography variant="body1" component="span">
                Not needed
              </Typography>
            </MenuItem>
          </Select>
        </FormControl>

        <FormControl size="medium" required>
          <InputLabel>Collateral</InputLabel>
          <Select
            label="Collateral"
            value={formData.collateral}
            onChange={(e) => handleChange("collateral", e.target.value)}
          >
            <MenuItem value="needed">
              <Typography variant="body1" component="span">
                Needed
              </Typography>
            </MenuItem>
            <MenuItem value="not_needed">
              <Typography variant="body1" component="span">
                Not needed
              </Typography>
            </MenuItem>
          </Select>
        </FormControl>

        <FormControl size="medium" required>
          <InputLabel>Trade Capture</InputLabel>
          <Select
            label="Trade Capture"
            value={formData.tradeCapture}
            onChange={(e) => handleChange("tradeCapture", e.target.value)}
          >
            <MenuItem value="needed">
              <Typography variant="body1" component="span">
                Needed
              </Typography>
            </MenuItem>
            <MenuItem value="not_needed">
              <Typography variant="body1" component="span">
                Not needed
              </Typography>
            </MenuItem>
          </Select>
        </FormControl>

        <FormControl size="medium" required>
          <InputLabel>Trade Confirmation</InputLabel>
          <Select
            label="Trade Confirmation"
            value={formData.tradeConfirmation}
            onChange={(e) => handleChange("tradeConfirmation", e.target.value)}
          >
            <MenuItem value="needed">
              <Typography variant="body1" component="span">
                Needed
              </Typography>
            </MenuItem>
            <MenuItem value="not_needed">
              <Typography variant="body1" component="span">
                Not needed
              </Typography>
            </MenuItem>
          </Select>
        </FormControl>

        <SignOffNoteField
          value={formData.signOffNote}
          onChange={(value) => handleChange("signOffNote", value)}
        />

        <CommentsSection
          loading={isSaving}
          comments={formData.comments}
          currentComment={currentComment}
          onCurrentCommentChange={setCurrentComment}
          saveComment={addComment}
          onDeleteComment={deleteComment}
        />
      </Box>
    </LifecycleDialog>
  );
};
